import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import SafeIcon from '../../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';

const { FiPlus, FiMinus } = FiIcons;

const FAQSection = () => {
  const [openIndex, setOpenIndex] = useState(null);

  const faqs = [
    {
      question: "What industries do you specialize in?",
      answer: "We have worked across e-commerce, real estate, education, technology, healthcare, and more. Our team adapts strategies to fit your specific industry needs."
    },
    {
      question: "How long before I see results?",
      answer: "Most campaigns begin showing measurable improvements within the first 30 days. However, significant growth typically occurs within 60-90 days as we optimize based on data."
    },
    {
      question: "Can you customize a package for my business?",
      answer: "Yes, we tailor all services to your business needs and goals. We offer flexible packages and can create custom solutions based on your budget and requirements."
    },
    {
      question: "Do you provide detailed reporting?",
      answer: "Absolutely! We provide comprehensive monthly reports with detailed analytics, ROI tracking, and actionable insights to help you understand campaign performance."
    },
    {
      question: "What makes your AI-powered approach different?",
      answer: "Our AI systems continuously analyze performance data, optimize targeting, and automate bid management to maximize your ROI while reducing manual work and human error."
    }
  ];

  const toggleFAQ = (index) => {
    setOpenIndex(openIndex === index ? null : index);
  };

  return (
    <section className="py-24 relative" style={{ backgroundColor: '#3a1c40' }}>
      {/* Animated background */}
      <motion.div 
        className="absolute inset-0 w-full h-full opacity-10"
        style={{ 
          background: 'radial-gradient(circle at 30% 50%, rgba(177, 107, 189, 0.4) 0%, rgba(58, 28, 64, 0) 60%), radial-gradient(circle at 70% 20%, rgba(255, 215, 0, 0.3) 0%, rgba(58, 28, 64, 0) 60%)',
        }}
        animate={{
          backgroundPosition: ['0% 0%', '10% 10%', '0% 0%'],
        }}
        transition={{
          duration: 15,
          repeat: Infinity,
          ease: "easeInOut"
        }}
      />
      
      <div className="max-w-4xl mx-auto px-6 relative z-10">
        <motion.div
          initial={{ y: 30, opacity: 0 }}
          whileInView={{ y: 0, opacity: 1 }}
          transition={{ duration: 0.8 }}
          viewport={{ once: true }}
          className="text-center mb-16"
        >
          <h2 className="text-4xl md:text-5xl font-bold mb-6 bg-gradient-to-r from-yellow-300 via-purple-300 to-yellow-300 bg-clip-text text-transparent">
            Frequently Asked Questions
          </h2>
        </motion.div>

        <motion.div 
          className="space-y-6"
          initial="closed"
          animate="open"
          variants={{
            open: {
              transition: { staggerChildren: 0.1 }
            },
            closed: {}
          }}
        >
          {faqs.map((faq, index) => (
            <motion.div
              key={index}
              initial={{ y: 30, opacity: 0 }}
              whileInView={{ y: 0, opacity: 1 }}
              transition={{ duration: 0.6, delay: index * 0.1 }}
              viewport={{ once: true }}
              className="bg-purple-900 bg-opacity-30 backdrop-blur-sm rounded-2xl overflow-hidden border border-purple-800"
              whileHover={{ 
                boxShadow: '0 10px 30px rgba(177, 107, 189, 0.2)',
                borderColor: openIndex === index ? '#FFD700' : 'rgba(177, 107, 189, 0.5)'
              }}
            >
              <motion.button
                onClick={() => toggleFAQ(index)}
                className="w-full px-8 py-6 text-left flex items-center justify-between transition-colors duration-300"
                whileHover={{ backgroundColor: 'rgba(177, 107, 189, 0.1)' }}
              >
                <h3 className="text-lg font-semibold pr-8 text-purple-100">
                  {faq.question}
                </h3>
                <motion.div 
                  className="p-2 rounded-full flex-shrink-0 transition-all duration-300"
                  animate={{ 
                    rotate: openIndex === index ? 180 : 0,
                    backgroundColor: openIndex === index ? '#FFD700' : 'rgba(177, 107, 189, 0.5)'
                  }}
                >
                  <SafeIcon 
                    icon={openIndex === index ? FiMinus : FiPlus} 
                    className="text-lg" 
                    style={{ color: openIndex === index ? '#3a1c40' : '#FFFFFF' }}
                  />
                </motion.div>
              </motion.button>
              <AnimatePresence>
                {openIndex === index && (
                  <motion.div
                    initial={{ height: 0, opacity: 0 }}
                    animate={{ 
                      height: 'auto', 
                      opacity: 1,
                      transition: {
                        height: {
                          duration: 0.4,
                          ease: "easeInOut"
                        },
                        opacity: {
                          duration: 0.25,
                          delay: 0.15
                        }
                      }
                    }}
                    exit={{ 
                      height: 0, 
                      opacity: 0,
                      transition: {
                        height: {
                          duration: 0.4,
                          ease: "easeInOut"
                        },
                        opacity: {
                          duration: 0.25
                        }
                      }
                    }}
                    className="overflow-hidden bg-purple-900 bg-opacity-20"
                  >
                    <motion.div 
                      className="px-8 pb-6"
                      initial={{ y: -20 }}
                      animate={{ y: 0 }}
                      transition={{ duration: 0.3, delay: 0.2 }}
                    >
                      <p className="text-purple-200 leading-relaxed">
                        {faq.answer}
                      </p>
                    </motion.div>
                  </motion.div>
                )}
              </AnimatePresence>
            </motion.div>
          ))}
        </motion.div>
      </div>
    </section>
  );
};

export default FAQSection;